import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { Layout, Badge, Icon, Card, Empty, message, Modal, Input } from 'antd'
import moment from 'moment'
import axios from 'util/Api'
import FilterBar from './FilterBar'
import FilterChips from './FilterChips'
import AlertBoard from './AlertBoard'
import AlertDrawer from './AlertDrawer'
import { THEME_TYPE_DARK } from '../../constants/ThemeSetting'
import {
    getFilterValue,
    getCurrentUser,
    formatDuration,
    isOverdue,
    stripHtml,
    resolveAlertTypeValue,
    isClosedStatus
} from './utils'

const { Content } = Layout
const { TextArea } = Input

const getAlertKey = (alert) => String((alert && (alert._id || alert.id)) || '')
const getRefId = (value) => {
    if (!value) return null
    if (typeof value === 'string' || typeof value === 'number') return String(value)
    if (value._id) return String(value._id)
    if (value.id) return String(value.id)
    return null
}

const getLoggedInUserData = () => {
    try {
        const raw = localStorage.getItem('user')
        if (!raw || raw === 'undefined') return null
        return JSON.parse(raw)
    } catch (err) {
        return null
    }
}

const toIdSet = (items = []) => {
    const ids = (items || []).map((item) => getRefId(item)).filter(Boolean)
    return new Set(ids)
}

const hasAssetLocationClientAccess = (alert, userLocationIds, userClientIds) => {
    const hasAsset = alert && alert.asset && alert.asset !== null
    if (!hasAsset) {
        const hasEntityContext = alert && alert.entity && alert.entityRefModel
        if (hasEntityContext) {
            // TODO: Add entity/entityRefModel-specific access validation rules.
            return true
        }
        return false
    }

    const assetLocationId = getRefId(alert.asset && alert.asset.location)
    const assetClientId = getRefId(alert.asset && alert.asset.client)
    const userHasLocationScope = userLocationIds.size > 0
    const userHasClientScope = userClientIds.size > 0

    // If user has no explicit location/client scope data, do not block at UI level.
    if (!userHasLocationScope && !userHasClientScope) {
        return true
    }

    // Compare location only when both asset location and user location-scope are available.
    if (assetLocationId && userHasLocationScope && !userLocationIds.has(assetLocationId)) {
        return false
    }

    // Compare client only when both asset client and user client-scope are available.
    if (assetClientId && userHasClientScope && !userClientIds.has(assetClientId)) {
        return false
    }

    return true
}

const canCurrentUserViewAlert = (alert, userContext) => {
    if (!alert || !userContext || !userContext.userId) return false
    // WHO may see an alert — observers, the current and original assignee, and the
    // supervisor of each (Alert Escalation Matrix) — is decided authoritatively by
    // the backend (/alerts/non-closed) and cannot be fully reproduced on the client
    // (it needs the supervisor graph). Trust that here and only re-apply the
    // location/client scope the client can compute.
    return hasAssetLocationClientAccess(alert, userContext.locationIds, userContext.clientIds)
}

const getUserName = (value) => {
    if (!value) return ''
    if (typeof value === 'string') return ''
    return value.name || value.username || value.email || ''
}

// Resolve the effective (scope-matched) assignees for an alert. The assignee
// list can carry location/client-scoped entries; the ones that apply to this
// alert are those matching the asset's location/client (with fallbacks).
const resolveAlertMatchedAssignees = (alertPayload) => {
    const assigneeList = Array.isArray(alertPayload && alertPayload.assignee) ? alertPayload.assignee : []
    if (assigneeList.length <= 0) return []

    const normalized = assigneeList.map((item) => ({
        userId: getRefId(item && item.user),
        userName: getUserName(item && item.user),
        locationId: getRefId(item && item.location),
        clientId: getRefId(item && item.client),
    })).filter((item) => Boolean(item.userName) || Boolean(item.userId))

    if (normalized.length <= 0) return []

    const assetLocationId = getRefId(alertPayload && alertPayload.asset && alertPayload.asset.location)
        || getRefId(alertPayload && alertPayload.entity && alertPayload.entity.location)
    const assetClientId = getRefId(alertPayload && alertPayload.asset && alertPayload.asset.client)
        || getRefId(alertPayload && alertPayload.entity && alertPayload.entity.client)

    if (!assetLocationId && !assetClientId) {
        return normalized.filter((item) => !item.locationId && !item.clientId)
    }

    const exactLocationExactClient = normalized.filter((item) => item.locationId === assetLocationId && item.clientId && item.clientId === assetClientId)
    const exactLocationAllClient = normalized.filter((item) => item.locationId === assetLocationId && !item.clientId)
    const allLocationExactClient = normalized.filter((item) => !item.locationId && item.clientId && item.clientId === assetClientId)
    const allLocationAllClient = normalized.filter((item) => !item.locationId && !item.clientId)

    return exactLocationExactClient.length > 0
        ? exactLocationExactClient
        : exactLocationAllClient.length > 0
            ? exactLocationAllClient
            : allLocationExactClient.length > 0
                ? allLocationExactClient
                : allLocationAllClient
}

const resolveAlertAssigneeDisplayName = (alertPayload) => {
    const matched = resolveAlertMatchedAssignees(alertPayload)
    const names = Array.from(new Set(matched.map((item) => item.userName).filter(Boolean)))
    return names.length > 0 ? names.join(', ') : 'Unassigned'
}

// User ids of the effective assignees, used by the "My Alerts" filter.
const resolveAlertAssigneeUserIds = (alertPayload) => {
    const matched = resolveAlertMatchedAssignees(alertPayload)
    return Array.from(new Set(matched.map((item) => item.userId).filter(Boolean)))
}

// Normalize raw assignee records (with assignTime, assignedBy, remarks) sorted
// most-recent first. Used for the assignment log and current-assignee display.
const normalizeAssigneeRecords = (alertPayload) => {
    const assigneeList = Array.isArray(alertPayload && alertPayload.assignee) ? alertPayload.assignee : []
    return assigneeList
        .map((item, index) => ({
            userId: getRefId(item && item.user),
            userName: getUserName(item && item.user),
            assignedByName: getUserName(item && item.assignedBy),
            remarks: (item && item.remarks) || '',
            assignTime: item && typeof item.assignTime === 'number' ? item.assignTime : null,
            // Original array position ($push order = assignment order). Preserved so
            // the ORIGINAL assignee can be found by first-assigned regardless of the
            // assignTime-based sort below (legacy records have no assignTime).
            order: index,
        }))
        .sort((a, b) => (b.assignTime || 0) - (a.assignTime || 0))
}

// The current assignee is the most recently added record (by assignTime).
const resolveAlertCurrentAssignee = (alertPayload) => {
    const records = normalizeAssigneeRecords(alertPayload).filter((item) => item.assignTime && item.userName)
    return records.length > 0 ? records[0] : null
}

function AlertInbox({ themeType }) {
    const isDark = themeType === THEME_TYPE_DARK
    const surface = isDark ? 'rgba(67, 79, 90, 0.96)' : 'rgba(255, 255, 255, 0.9)'
    const border = isDark ? '#495762' : '#dfe1e6'
    const muted = isDark ? '#a1a1a1' : 'rgba(0,0,0,0.45)'
    const strong = isDark ? '#E0E0E0' : '#172b4d'
    const brand = isDark ? '#7fb3ff' : '#1890ff'
    const pageBackground = isDark
        ? 'radial-gradient(circle at top left, #3f4a54 0, #38424B 55%, #2f3942 100%)'
        : 'radial-gradient(circle at top left, #dde7ff 0, #f4f5f7 45%, #ffffff 100%)'
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [myAlertsOnly, setMyAlertsOnly] = useState(false)
    const [activeFilterTypes, setActiveFilterTypes] = useState(['alertType'])
    const [enabledFilterValues, setEnabledFilterValues] = useState({})
    const [alertDateRange, setAlertDateRange] = useState([null, null])
    const [expandedColumn, setExpandedColumn] = useState(null)
    const [drawerVisible, setDrawerVisible] = useState(false)
    const [drawerExpanded, setDrawerExpanded] = useState(false)
    const [selectedAlert, setSelectedAlert] = useState(null)
    const [activeDrawerAlertId, setActiveDrawerAlertId] = useState(null)
    const [data, setData] = useState([])
    const [bulkIgnoreLoading, setBulkIgnoreLoading] = useState(false)
    const [bulkDirectActionLoading, setBulkDirectActionLoading] = useState(false)
    // Bulk action (per-section) modal state
    const [bulkModalVisible, setBulkModalVisible] = useState(false)
    const [bulkColumnId, setBulkColumnId] = useState(null)
    const [bulkAlerts, setBulkAlerts] = useState([])
    const [bulkAnalysisText, setBulkAnalysisText] = useState('')
    const [bulkActionSuggestedText, setBulkActionSuggestedText] = useState('')
    const [bulkActionTakenText, setBulkActionTakenText] = useState('')
    const [bulkActionLoading, setBulkActionLoading] = useState(false)

    // Initialize default filters (skip alertDate - it uses date range, not discrete values)
    const initializeDefaultFilters = useCallback((alerts) => {
        const newEnabledFilterValues = {}
        activeFilterTypes.forEach((filterType) => {
            if (filterType === 'alertDate') return
            if (!newEnabledFilterValues[filterType] || !Array.isArray(newEnabledFilterValues[filterType]) || newEnabledFilterValues[filterType].length === 0) {
                const allValues = new Set()
                alerts.forEach((c) => {
                    if (isClosedStatus(c.status)) return
                    const val = getFilterValue(c, filterType)
                    if (val) allValues.add(val)
                })
                newEnabledFilterValues[filterType] = Array.from(allValues)
            }
        })
        setEnabledFilterValues(newEnabledFilterValues)
    }, [activeFilterTypes])

    // Load data from API
    // options.showSpinner (default true) toggles the full-board loading spinner.
    // options.resetFilters (default true) re-initialises the filter chips; pass
    // false to reconcile with the server while preserving the user's selections.
    const loadData = useCallback(async (options = {}) => {
        const { showSpinner = true, resetFilters = true } = options
        try {
            if (showSpinner) setLoading(true)
            const response = await axios.get('/alerts/non-closed')

            // Handle different response formats
            let apiData = []
            if (response && response.data) {
                if (Array.isArray(response.data)) {
                    apiData = response.data
                } else if (response.data && Array.isArray(response.data.data)) {
                    apiData = response.data.data
                } else if (response.data && Array.isArray(response.data.resources)) {
                    apiData = response.data.resources
                } else {
                    apiData = []
                }
            }

            const loggedInUser = getLoggedInUserData()
            const userContext = {
                userId: getRefId(loggedInUser && (loggedInUser._id || loggedInUser.id)),
                locationIds: toIdSet(loggedInUser && loggedInUser.locations),
                clientIds: toIdSet(loggedInUser && loggedInUser.clients),
            }
            const visibleAlerts = apiData.filter((alert) => canCurrentUserViewAlert(alert, userContext))

            // Map API response to alert format and convert dates to moment objects
            const mappedAlerts = visibleAlerts.map((alert) => {
                // Convert date strings to moment objects
                const createdAt = alert.createdAt ? moment(alert.createdAt) : moment()
                const dueAt = alert.alertDueDateTime ? moment(alert.alertDueDateTime) : null

                // Convert statusHistory timestamps to moment objects
                const statusHistory = (alert.statusHistory || []).map((history) => ({
                    ...history,
                    timestamp: history.date ? moment(history.date) : moment(),
                    timestampFormatted: history.date
                        ? moment(history.date).format('DD MMM YYYY, HH:mm:ss')
                        : moment().format('DD MMM YYYY, HH:mm:ss')
                }))

                // Check if asset exists, otherwise check entity and entityRefModel
                const hasAsset = alert.asset && alert.asset !== null
                const isManpower = !hasAsset && alert.entityRefModel === 'Manpower' && alert.entity
                const manpowerName = isManpower ? (alert.entity.name || '') : ''

                // Resolve "action taken" content from the most recent statusHistory
                // entry's analysisId (the source of truth for the text). Falls back to
                // any top-level field.
                let actionTakenValue = alert.actionTaken || ''
                const sortedHistoryForAction = [...statusHistory].sort((a, b) => {
                    const timeA = a.timestamp ? moment(a.timestamp) : moment(0)
                    const timeB = b.timestamp ? moment(b.timestamp) : moment(0)
                    return timeB.valueOf() - timeA.valueOf()
                })
                const historyWithAnalysis = sortedHistoryForAction.find((h) => h.analysisId && typeof h.analysisId === 'object')
                if (historyWithAnalysis && historyWithAnalysis.analysisId.actionTaken) {
                    actionTakenValue = historyWithAnalysis.analysisId.actionTaken
                }

                // Build base alert object
                const baseAlert = {
                    _id: alert._id,
                    id: alert.alertId || '',
                    alertReference: alert.alertReference || '',
                    severity: alert.alertSeverity || 'LOW',
                    type: resolveAlertTypeValue(alert.alertTypeName, alert.alertType),
                    title: alert.alertTitle || alert.alertDescription || '',
                    body: alert.alertDescription || '',
                    site: alert.site || '',
                    status: alert.currentStatus || 'NEW',
                    // Current assignee = most recent by assignTime; fall back to the
                    // scope-matched assignee for legacy records without assignTime.
                    assignee: (resolveAlertCurrentAssignee(alert) || {}).userName || resolveAlertAssigneeDisplayName(alert),
                    assigneeUserIds: resolveAlertCurrentAssignee(alert)
                        ? [resolveAlertCurrentAssignee(alert).userId]
                        : resolveAlertAssigneeUserIds(alert),
                    assigneeRecords: normalizeAssigneeRecords(alert),
                    assignedTo: getRefId(alert.assignedTo),
                    // Server-authoritative: may the current user complete this alert
                    // (i.e. is the original assignee). Drives the complete buttons.
                    canComplete: alert.canComplete === true,
                    observers: (Array.isArray(alert.observers) ? alert.observers : [])
                        .map((o) => ({ id: getRefId(o), name: getUserName(o) }))
                        .filter((o) => o.id),
                    createdAt: createdAt,
                    alertDate: alert.alertDateTime ? moment(alert.alertDateTime) : null,
                    dueAt: dueAt,
                    analysisStarted: alert.statusHistory.find(status => status.status === 'UNDER_ANALYSIS') ? true : false,
                    analysisComplete: alert.statusHistory.find(status => status.status === 'ACTION_PENDING') ? true : false,
                    analysis: alert.analysis || '',
                    actionSuggested: alert.actionSuggested || alert.action_suggested || '',
                    actionTaken: actionTakenValue,
                    attachments: alert.alertAttachments || [],
                    documents: alert.documents || [],
                    statusHistory: statusHistory,
                    entityRefModel: alert.entityRefModel || null,
                    manpowerName: manpowerName
                }

                // Add asset-related fields if asset exists
                if (hasAsset) {
                    return {
                        ...baseAlert,
                        entityLabel: alert.asset.assetCode || '',
                        entityId: alert.asset.assetCode || '',
                        assetCode: alert.asset.assetCode || '',
                        ownerCode: alert.asset.ownerCode || '',
                        altName: alert.asset.altName || '',
                        location: alert.asset.location && alert.asset.location.name || '',
                        subLocation: alert.asset.client && alert.asset.client.name || '',
                        assetModel: alert.asset.assetModel && alert.asset.assetModel.name || '',
                        assetSubType: alert.asset.equipmentSubType && alert.asset.equipmentSubType.name || '',
                        assetType: alert.asset.equipmentSubType && alert.asset.equipmentSubType.assetType && alert.asset.equipmentSubType.assetType.name || ''
                    }
                } else {
                    // No asset - use entity fields or empty defaults
                    return {
                        ...baseAlert,
                        entityLabel: manpowerName || '',
                        entityId: manpowerName || '',
                        assetCode: '',
                        ownerCode: '',
                        altName: '',
                        location: '',
                        subLocation: '',
                        assetModel: '',
                        assetSubType: '',
                        assetType: ''
                    }
                }
            })
            // console.log(mappedAlerts)
            setData(mappedAlerts)
            if (resetFilters) initializeDefaultFilters(mappedAlerts)

            // Update alert count in localStorage
            const newAlertsCount = mappedAlerts.filter(alert => {
                const status = alert.status || ''
                return status.toUpperCase() === 'NEW' || status === 'New'
            }).length

            localStorage.setItem('newAlertsCount', newAlertsCount.toString())

            // Dispatch custom event for same-tab updates
            window.dispatchEvent(new CustomEvent('newAlertsCountUpdated', {
                detail: { count: newAlertsCount }
            }))
        } catch (error) {
            console.error('Error loading alerts:', error)
            message.error('Failed to load alerts. Please try again.')
            setData([])
        } finally {
            if (showSpinner) setLoading(false)
        }
    }, [initializeDefaultFilters])

    // Initialize data on mount
    useEffect(() => {
        loadData()
    }, [loadData])

    // Refresh alerts
    const handleRefresh = useCallback(async () => {
        await loadData()
        message.success('Alerts refreshed')
    }, [loadData])

    // Handle filter type toggle
    const handleFilterTypeChange = useCallback((filterType, isChecked) => {
        setActiveFilterTypes((prev) => {
            const newActiveTypes = isChecked
                ? [...prev, filterType].filter((v, i, arr) => arr.indexOf(v) === i)
                : prev.filter(ft => ft !== filterType)

            setEnabledFilterValues((prevEnabled) => {
                const newEnabled = { ...prevEnabled }

                if (isChecked && filterType !== 'alertDate') {
                    const allValues = new Set()
                    data.forEach((c) => {
                        if (isClosedStatus(c.status)) return
                        const val = getFilterValue(c, filterType)
                        if (val) allValues.add(val)
                    })
                    newEnabled[filterType] = Array.from(allValues)
                } else if (filterType !== 'alertDate') {
                    delete newEnabled[filterType]
                }

                return newEnabled
            })

            return newActiveTypes
        })
    }, [data])

    // Toggle filter value
    const toggleFilterValue = useCallback((filterType, value) => {
        setEnabledFilterValues((prev) => {
            const newEnabled = { ...prev }
            if (!newEnabled[filterType]) {
                newEnabled[filterType] = []
            }

            const enabledValues = new Set(newEnabled[filterType])
            if (enabledValues.has(value)) {
                enabledValues.delete(value)
            } else {
                enabledValues.add(value)
            }

            // Get all unique values for this filter type
            const allValues = new Set()
            data.forEach((c) => {
                const val = getFilterValue(c, filterType)
                if (val) allValues.add(val)
            })

            // If all are disabled, enable all (prevent empty state)
            if (enabledValues.size === 0) {
                allValues.forEach((v) => enabledValues.add(v))
            }

            newEnabled[filterType] = Array.from(enabledValues)
            return newEnabled
        })
    }, [data])

    // Select all filter values
    const selectAllFilterValues = useCallback((filterType) => {
        setEnabledFilterValues((prev) => {
            const newEnabled = { ...prev }
            const allValues = new Set()
            data.forEach((c) => {
                if (isClosedStatus(c.status)) return
                const val = getFilterValue(c, filterType)
                if (val) allValues.add(val)
            })
            newEnabled[filterType] = Array.from(allValues)
            return newEnabled
        })
    }, [data])

    // Select none filter values
    const selectNoneFilterValues = useCallback((filterType) => {
        setEnabledFilterValues((prev) => {
            const newEnabled = { ...prev }
            newEnabled[filterType] = []
            return newEnabled
        })
    }, [])

    // Get dynamic count for filter chips
    const getDynamicCount = useCallback((filterType, value) => {
        let count = 0

        data.forEach((alert) => {
            if (isClosedStatus(alert.status)) return

            const cardValue = getFilterValue(alert, filterType)
            if (cardValue !== value) return

            const q = (searchTerm || '').trim().toLowerCase()
            if (q) {
                const bodyText = stripHtml(alert.body || '')
                const titleText = stripHtml(alert.title || '')
                const bodySearchable = (titleText + ' ' + bodyText).toLowerCase()
                const fieldHaystack = (
                    (alert.id || '') + ' ' + (alert.assetCode || '') + ' ' + (alert.ownerCode || '') +
                    ' ' + (alert.altName || '') + ' ' + (alert.manpowerName || '') +
                    ' ' + (alert.title || '') + ' ' + (alert.entityId || '') + ' ' + (alert.location || '')
                ).toLowerCase()
                if (!bodySearchable.includes(q) && !fieldHaystack.includes(q)) return
            }

            let matchesOtherFilters = true
            for (const otherFilterType of activeFilterTypes) {
                if (otherFilterType === filterType) continue
                if (otherFilterType === 'alertDate') {
                    const start = alertDateRange && alertDateRange[0]
                    const end = alertDateRange && alertDateRange[1]
                    if (!start && !end) continue
                    const alertDate = alert.alertDate
                    if (!alertDate) {
                        matchesOtherFilters = false
                        break
                    }
                    const d = moment(alertDate)
                    if (start && d.isBefore(start)) {
                        matchesOtherFilters = false
                        break
                    }
                    if (end && d.isAfter(end)) {
                        matchesOtherFilters = false
                        break
                    }
                    continue
                }

                const cardOtherValue = getFilterValue(alert, otherFilterType)
                if (!enabledFilterValues[otherFilterType] || !Array.isArray(enabledFilterValues[otherFilterType])) {
                    continue
                }

                const enabledOtherValues = new Set(enabledFilterValues[otherFilterType])
                const allOtherValues = new Set()
                data.forEach((c) => {
                    if (c.status === 'ACTION_TAKEN') return
                    const val = getFilterValue(c, otherFilterType)
                    if (val) allOtherValues.add(val)
                })

                if (enabledOtherValues.size === 0) {
                    matchesOtherFilters = false
                    break
                } else if (enabledOtherValues.size < allOtherValues.size) {
                    if (!enabledOtherValues.has(cardOtherValue)) {
                        matchesOtherFilters = false
                        break
                    }
                }
            }

            if (matchesOtherFilters) {
                count++
            }
        })

        return count
    }, [data, searchTerm, activeFilterTypes, enabledFilterValues, alertDateRange])

    // Get filtered data
    const filteredData = useMemo(() => {
        const loggedInUser = getLoggedInUserData()
        const currentUserId = getRefId(loggedInUser && (loggedInUser._id || loggedInUser.id))
        return data.filter((r) => {
            if (isClosedStatus(r.status)) return false

            // "My Alerts": keep only alerts where the current user is an assignee.
            if (myAlertsOnly) {
                const assigneeIds = Array.isArray(r.assigneeUserIds) ? r.assigneeUserIds : []
                if (!currentUserId || !assigneeIds.includes(currentUserId)) return false
            }

            const q = (searchTerm || '').trim().toLowerCase()
            const matchesQ = (() => {
                if (!q) return true
                const bodyText = stripHtml(r.body || '')
                const titleText = stripHtml(r.title || '')
                const bodySearchable = (titleText + ' ' + bodyText).toLowerCase()
                if (bodySearchable.includes(q)) return true
                const fieldHaystack = (
                    (r.id || '') + ' ' + (r.assetCode || '') + ' ' + (r.ownerCode || '') +
                    ' ' + (r.altName || '') + ' ' + (r.manpowerName || '') +
                    ' ' + (r.title || '') + ' ' + (r.entityId || '') + ' ' + (r.location || '')
                ).toLowerCase()
                return fieldHaystack.includes(q)
            })()

            if (!matchesQ) return false

            for (const filterType of activeFilterTypes) {
                if (filterType === 'alertDate') {
                    const start = alertDateRange && alertDateRange[0]
                    const end = alertDateRange && alertDateRange[1]
                    if (!start && !end) continue
                    const alertDate = r.alertDate
                    if (!alertDate) return false
                    const d = moment(alertDate)
                    if (start && d.isBefore(start)) return false
                    if (end && d.isAfter(end)) return false
                    continue
                }

                const cardValue = getFilterValue(r, filterType)
                if (!enabledFilterValues[filterType] || !Array.isArray(enabledFilterValues[filterType])) {
                    continue
                }

                const enabledValues = new Set(enabledFilterValues[filterType])
                const allValues = new Set()
                data.forEach((c) => {
                    if (isClosedStatus(c.status)) return
                    const val = getFilterValue(c, filterType)
                    if (val) allValues.add(val)
                })

                if (enabledValues.size === 0) {
                    return false
                } else if (enabledValues.size < allValues.size) {
                    if (!enabledValues.has(cardValue)) {
                        return false
                    }
                }
            }

            return true
        })
    }, [data, searchTerm, activeFilterTypes, enabledFilterValues, alertDateRange, myAlertsOnly])

    const isAlertEligibleForIgnore = useCallback((alert) => {
        const status = String(alert.status || '').toUpperCase()
        return status === 'NEW'
    }, [])

    const alertsByReference = useMemo(() => {
        const refMap = new Map()
        data.forEach((alert) => {
            if (!alert) return
            const status = (alert.status || '').toUpperCase()
            if (isClosedStatus(status)) return
            const reference = (alert.alertReference || '').trim()
            if (!reference) return
            if (!refMap.has(reference)) {
                refMap.set(reference, [])
            }
            refMap.get(reference).push(alert)
        })
        return refMap
    }, [data])

    const relatedCountsByAlertId = useMemo(() => {
        const counts = {}
        alertsByReference.forEach((alerts) => {
            const relatedCount = Math.max((alerts || []).length - 1, 0)
            alerts.forEach((alert) => {
                const key = getAlertKey(alert)
                if (key) {
                    counts[String(key)] = relatedCount
                }
            })
        })
        return counts
    }, [alertsByReference])

    const drawerAlerts = useMemo(() => {
        if (!selectedAlert) return []

        const reference = (selectedAlert.alertReference || '').trim()
        if (!reference) return [selectedAlert]

        const sameReferenceAlerts = alertsByReference.get(reference) || []
        if (!sameReferenceAlerts.length) return [selectedAlert]

        const groupedByType = sameReferenceAlerts.reduce((acc, item) => {
            const typeKey = item.type || 'UNKNOWN'
            if (!acc[typeKey]) {
                acc[typeKey] = []
            }
            acc[typeKey].push(item)
            return acc
        }, {})

        Object.keys(groupedByType).forEach((typeKey) => {
            groupedByType[typeKey] = groupedByType[typeKey].sort((a, b) => {
                const aTime = a.alertDate || a.createdAt
                const bTime = b.alertDate || b.createdAt
                return moment(bTime).valueOf() - moment(aTime).valueOf()
            })
        })

        const orderedTypes = Object.keys(groupedByType).sort((a, b) => a.localeCompare(b))

        const flattenedAlerts = []
        orderedTypes.forEach((typeKey) => {
            flattenedAlerts.push(...(groupedByType[typeKey] || []))
        })

        return flattenedAlerts.length ? flattenedAlerts : [selectedAlert]
    }, [selectedAlert, alertsByReference])

    const activeDrawerAlert = useMemo(() => {
        if (!drawerAlerts.length) return null
        const selected = drawerAlerts.find(
            (item) => getAlertKey(item) === String(activeDrawerAlertId || '')
        )
        return selected || drawerAlerts[0]
    }, [drawerAlerts, activeDrawerAlertId])

    // Get KPIs
    const kpis = useMemo(() => {
        const open = filteredData.length
        const criticalOpen = filteredData.filter((r) => r.severity === 'Critical').length
        const overdue = filteredData.filter((r) => isOverdue(r)).length
        const newToday = filteredData.filter((r) => moment(r.createdAt).isSame(moment(), 'day')).length
        return { open, criticalOpen, overdue, newToday }
    }, [filteredData])

    // Update alert status
    const updateAlertStatus = useCallback(async (record, newStatus, action, actionDetails = {}, options = {}) => {
        const isSilent = !!options.silent
        const now = moment()
        const currentUser = getCurrentUser()

        // Map status to API format
        const statusMap = {
            'NEW': 'NEW',
            'New': 'NEW',
            'UNDER_ANALYSIS': 'UNDER_ANALYSIS',
            'Under Analysis': 'UNDER_ANALYSIS',
            'ACTION_PENDING': 'ACTION_PENDING',
            'Action Pending': 'ACTION_PENDING',
            'ACTION_TAKEN': 'ACTION_TAKEN',
            'Action Taken': 'ACTION_TAKEN',
            'CLOSED': 'CLOSED',
            'Closed': 'CLOSED',
            'IGNORED': 'IGNORED',
            'Ignored': 'IGNORED',
            'DIRECT_ACTION': 'DIRECT_ACTION',
            'Direct Action': 'DIRECT_ACTION'
        }
        const apiStatus = statusMap[newStatus] || newStatus

        // Extract analysisId from statusHistory - look through ALL entries to find any analysisId
        // This ensures we capture analysisId from any previous status update
        let analysisId = null
        if (record.statusHistory && Array.isArray(record.statusHistory)) {
            // Sort statusHistory by timestamp (most recent first)
            const sortedHistory = [...record.statusHistory].sort((a, b) => {
                const timeA = a.timestamp ? (moment.isMoment(a.timestamp) ? a.timestamp : moment(a.timestamp)) : moment(0)
                const timeB = b.timestamp ? (moment.isMoment(b.timestamp) ? b.timestamp : moment(b.timestamp)) : moment(0)
                return timeB.valueOf() - timeA.valueOf()
            })

            // Look through all statusHistory entries (most recent first) to find analysisId
            for (const historyEntry of sortedHistory) {
                if (historyEntry.analysisId) {
                    analysisId = historyEntry.analysisId
                    break // Use the most recent one found
                }
            }
        }

        // Also check if analysisId exists directly on record (fallback)
        if (!analysisId && record.analysisId) {
            analysisId = record.analysisId
        }

        // Extract _id if analysisId is an object, otherwise use as is
        let analysisIdValue = null
        if (analysisId) {
            if (typeof analysisId === 'object' && analysisId._id) {
                analysisIdValue = analysisId._id
            } else if (typeof analysisId === 'string') {
                analysisIdValue = analysisId
            } else {
                analysisIdValue = analysisId
            }
        }

        // Prepare API payload - include analysisId if it exists
        const apiPayload = {
            currentStatus: apiStatus,
            analysis: actionDetails.analysis || record.analysis || '',
            actionSuggested: actionDetails.actionSuggested || record.actionSuggested || '',
            actionTaken: actionDetails.actionTaken || record.actionTaken || '',
            remarks: actionDetails.remarks || action
        }

        // Include analysisId if it exists
        if (analysisIdValue) {
            apiPayload.analysisId = analysisIdValue
        }

        let updatedAlert = null
        try {
            // Call API to update status on server and get updated alert
            const alertId = record._id || record.id
            if (!alertId) {
                if (!isSilent) {
                    message.error('Alert ID not found')
                }
                return false
            }
            const response = await axios.put(`/alerts/${alertId}/status`, apiPayload)

            // Extract the updated alert from response
            if (response && response.data) {
                updatedAlert = response.data.data || response.data.resource || response.data
            }
        } catch (error) {
            console.error('Error updating alert status:', error)
            if (!isSilent) {
                message.error('Failed to update alert status. Please try again.')
            }
            return false
        }

        // If we got the updated alert from API, use it to overwrite the existing alert
        if (updatedAlert) {
            // Map API response to our internal format (similar to loadData)
            const createdAt = updatedAlert.createdAt ? moment(updatedAlert.createdAt) : (record.createdAt || moment())
            const dueAt = updatedAlert.alertDueDate ? moment(updatedAlert.alertDueDate) : record.dueAt

            // Convert statusHistory timestamps to moment objects
            const statusHistory = (updatedAlert.statusHistory || []).map((history) => ({
                ...history,
                timestamp: history.date ? moment(history.date) : moment(),
                timestampFormatted: history.date
                    ? moment(history.date).format('DD MMM YYYY, HH:mm:ss')
                    : moment().format('DD MMM YYYY, HH:mm:ss')
            }))

            // Extract analysisId from statusHistory - look through ALL entries to find any analysisId
            // This ensures we capture analysisId from any status update, not just the current one
            let analysisId = null
            // First, try to find analysisId in the most recent statusHistory entry
            const sortedHistory = [...statusHistory].sort((a, b) => {
                const timeA = a.timestamp ? moment(a.timestamp) : moment(0)
                const timeB = b.timestamp ? moment(b.timestamp) : moment(0)
                return timeB.valueOf() - timeA.valueOf()
            })

            // Look through all statusHistory entries (most recent first) to find analysisId
            for (const historyEntry of sortedHistory) {
                if (historyEntry.analysisId) {
                    analysisId = historyEntry.analysisId
                    break // Use the most recent one found
                }
            }

            // Fallback: also check current status entry specifically
            if (!analysisId) {
                const currentStatusHistory = statusHistory
                    .filter(h => (h.status === updatedAlert.currentStatus || h.status === apiStatus))
                    .sort((a, b) => {
                        const timeA = a.timestamp ? moment(a.timestamp) : moment(0)
                        const timeB = b.timestamp ? moment(b.timestamp) : moment(0)
                        return timeB.valueOf() - timeA.valueOf()
                    })[0]

                if (currentStatusHistory && currentStatusHistory.analysisId) {
                    analysisId = currentStatusHistory.analysisId
                }
            }

            // Check if asset exists, otherwise check entity and entityRefModel
            const hasAsset = updatedAlert.asset && updatedAlert.asset !== null
            const isManpower = !hasAsset && updatedAlert.entityRefModel === 'Manpower' && updatedAlert.entity
            const manpowerName = isManpower ? (updatedAlert.entity.name || '') : (record.manpowerName || '')

            // Extract analysis, actionSuggested, and actionTaken from analysisId if it exists
            let analysisFromHistory = updatedAlert.analysis || record.analysis || ''
            let actionSuggestedFromHistory = updatedAlert.actionSuggested || updatedAlert.action_suggested || record.actionSuggested || ''
            let actionTakenFromHistory = record.actionTaken || ''

            // If analysisId exists, extract the data from it
            if (analysisId) {
                // analysisId can be an object with _id or the full object
                const analysisIdObj = typeof analysisId === 'object' && analysisId._id ? analysisId : analysisId

                // Extract from analysisId object if it has the fields
                if (analysisIdObj && typeof analysisIdObj === 'object') {
                    if (analysisIdObj.analysis !== undefined && analysisIdObj.analysis !== null && analysisIdObj.analysis !== '') {
                        analysisFromHistory = analysisIdObj.analysis
                    }
                    if (analysisIdObj.actionSuggested !== undefined && analysisIdObj.actionSuggested !== null && analysisIdObj.actionSuggested !== '') {
                        actionSuggestedFromHistory = analysisIdObj.actionSuggested
                    }
                    if (analysisIdObj.actionTaken !== undefined && analysisIdObj.actionTaken !== null && analysisIdObj.actionTaken !== '') {
                        actionTakenFromHistory = analysisIdObj.actionTaken
                    }
                }
            }

            // The actionTaken TEXT comes from analysisId (handled above). The
            // ACTION_TAKEN status-history entry's `remarks` is the action label, not
            // the text, so it must not override the resolved value.

            // Build base alert object
            const baseAlert = {
                _id: updatedAlert._id || record._id,
                id: updatedAlert.alertId || record.id || '',
                alertReference: updatedAlert.alertReference || record.alertReference || '',
                severity: updatedAlert.alertSeverity || record.severity || 'LOW',
                type: resolveAlertTypeValue(updatedAlert.alertTypeName, updatedAlert.alertType) || record.type || '',
                title: updatedAlert.alertTitle || updatedAlert.alertDescription || record.title || '',
                body: updatedAlert.alertDescription || record.body || '',
                site: updatedAlert.site || record.site || '',
                status: updatedAlert.currentStatus || apiStatus,
                assignee: (resolveAlertCurrentAssignee(updatedAlert) || {}).userName
                    || (updatedAlert.assignee ? resolveAlertAssigneeDisplayName(updatedAlert) : null)
                    || record.assignee || 'Unassigned',
                assigneeUserIds: (updatedAlert.assignee
                    ? (resolveAlertCurrentAssignee(updatedAlert) ? [resolveAlertCurrentAssignee(updatedAlert).userId] : resolveAlertAssigneeUserIds(updatedAlert))
                    : null) || record.assigneeUserIds || [],
                assigneeRecords: (updatedAlert.assignee ? normalizeAssigneeRecords(updatedAlert) : null) || record.assigneeRecords || [],
                assignedTo: getRefId(updatedAlert.assignedTo) || record.assignedTo || null,
                canComplete: typeof updatedAlert.canComplete === 'boolean' ? updatedAlert.canComplete : (record.canComplete === true),
                observers: (updatedAlert.observers ? (Array.isArray(updatedAlert.observers) ? updatedAlert.observers : []).map((o) => ({ id: getRefId(o), name: getUserName(o) })).filter((o) => o.id) : null) || record.observers || [],
                createdAt: createdAt,
                dueAt: dueAt,
                analysisStarted: statusHistory.find(status => status.status === 'UNDER_ANALYSIS') ? true : false,
                analysisComplete: statusHistory.find(status => status.status === 'ACTION_PENDING') ? true : false,
                analysis: analysisFromHistory,
                actionSuggested: actionSuggestedFromHistory,
                actionTaken: actionTakenFromHistory,
                attachments: updatedAlert.alertAttachments || record.attachments || [],
                documents: updatedAlert.documents || record.documents || [],
                statusHistory: statusHistory,
                analysisId: analysisId || record.analysisId || null,
                entityRefModel: updatedAlert.entityRefModel || record.entityRefModel || null,
                manpowerName: manpowerName
            }

            // Add asset-related fields if asset exists, otherwise use entity/manpower fields
            const mappedAlert = hasAsset ? {
                ...baseAlert,
                entityLabel: updatedAlert.asset.assetCode || record.entityLabel || '',
                entityId: updatedAlert.asset.assetCode || record.entityId || '',
                assetCode: updatedAlert.asset.assetCode || record.assetCode || '',
                ownerCode: updatedAlert.asset.ownerCode || record.ownerCode || '',
                altName: updatedAlert.asset.altName || record.altName || '',
                location: updatedAlert.asset.location && updatedAlert.asset.location.name || record.location || '',
                subLocation: updatedAlert.asset.client && updatedAlert.asset.client.name || record.subLocation || '',
                assetModel: updatedAlert.asset.assetModel && updatedAlert.asset.assetModel.name || record.assetModel || '',
                assetSubType: updatedAlert.asset.equipmentSubType && updatedAlert.asset.equipmentSubType.name || record.assetSubType || '',
                assetType: updatedAlert.asset.equipmentSubType && updatedAlert.asset.equipmentSubType.assetType && updatedAlert.asset.equipmentSubType.assetType.name || record.assetType || ''
            } : {
                ...baseAlert,
                entityLabel: manpowerName || record.entityLabel || '',
                entityId: manpowerName || record.entityId || '',
                assetCode: record.assetCode || '',
                ownerCode: record.ownerCode || '',
                altName: record.altName || '',
                location: record.location || '',
                subLocation: record.subLocation || '',
                assetModel: record.assetModel || '',
                assetSubType: record.assetSubType || '',
                assetType: record.assetType || ''
            }

            // Update the data state with the mapped alert
            setData((prevData) => {
                const updatedData = prevData.map((r) => {
                    if (!r || (r.id !== mappedAlert.id && r._id !== mappedAlert._id)) return r
                    return mappedAlert
                })

                // Check if status changed from NEW to something else
                const oldStatus = record.status || ''
                const wasNew = oldStatus.toUpperCase() === 'NEW' || oldStatus === 'New'
                const isNew = (mappedAlert.status || '').toUpperCase() === 'NEW' || mappedAlert.status === 'New'

                // If status changed from New to something else, update count
                if (wasNew && !isNew) {
                    // Count remaining New alerts
                    const newAlertsCount = updatedData.filter(alert => {
                        const status = alert.status || ''
                        return status.toUpperCase() === 'NEW' || status === 'New'
                    }).length

                    // Update localStorage
                    localStorage.setItem('newAlertsCount', newAlertsCount.toString())

                    // Dispatch custom event for same-tab updates
                    window.dispatchEvent(new CustomEvent('newAlertsCountUpdated', {
                        detail: { count: newAlertsCount }
                    }))
                }

                return updatedData
            })

            // Update selectedAlert if it's the same alert
            if (selectedAlert && (selectedAlert.id === mappedAlert.id || selectedAlert._id === mappedAlert._id)) {
                setSelectedAlert(mappedAlert)
            }
        } else {
            // Fallback: if API doesn't return updated alert, update locally (old behavior)
            const previousHistory = record.statusHistory || []
            let durationInPreviousStatus = null
            if (previousHistory.length > 0) {
                const lastChange = previousHistory[previousHistory.length - 1]
                const duration = now.diff(moment(lastChange.timestamp), 'minutes')
                durationInPreviousStatus = {
                    minutes: duration,
                    hours: Math.floor(duration / 60),
                    days: Math.floor(duration / (60 * 24)),
                    humanReadable: formatDuration(duration)
                }
            } else if (record.createdAt) {
                const duration = now.diff(moment(record.createdAt), 'minutes')
                durationInPreviousStatus = {
                    minutes: duration,
                    hours: Math.floor(duration / 60),
                    days: Math.floor(duration / (60 * 24)),
                    humanReadable: formatDuration(duration)
                }
            }

            const statusChange = {
                from: record.status,
                to: apiStatus,
                action: action,
                timestamp: now,
                timestampFormatted: now.format('DD MMM YYYY, HH:mm:ss'),
                userId: currentUser,
                userName: currentUser,
                durationInPreviousStatus: durationInPreviousStatus,
                ...actionDetails
            }

            setData((prevData) => {
                const updatedData = prevData.map((r) => {
                    if (!r || r.id !== record.id) return r
                    return {
                        ...r,
                        status: apiStatus,
                        statusHistory: [...(r.statusHistory || []), statusChange],
                        analysisStarted: apiStatus === 'UNDER_ANALYSIS' ? true : (r.analysisStarted || false),
                        analysisComplete: apiStatus === 'ACTION_PENDING' ? true : (r.analysisComplete || false),
                        lastStatusChangeTime: now,
                        ...actionDetails
                    }
                })

                // Check if status changed from NEW to something else
                const oldStatus = record.status || ''
                const wasNew = oldStatus.toUpperCase() === 'NEW' || oldStatus === 'New'
                const isNew = apiStatus.toUpperCase() === 'NEW'

                // If status changed from New to something else, update count
                if (wasNew && !isNew) {
                    // Count remaining New alerts
                    const newAlertsCount = updatedData.filter(alert => {
                        const status = alert.status || ''
                        return status.toUpperCase() === 'NEW' || status === 'New'
                    }).length

                    // Update localStorage
                    localStorage.setItem('newAlertsCount', newAlertsCount.toString())

                    // Dispatch custom event for same-tab updates
                    window.dispatchEvent(new CustomEvent('newAlertsCountUpdated', {
                        detail: { count: newAlertsCount }
                    }))
                }

                return updatedData
            })

            // Update selectedAlert in fallback case - merge with updated data
            if (selectedAlert && (selectedAlert.id === record.id || selectedAlert._id === record._id)) {
                const updatedRecord = {
                    ...record,
                    status: apiStatus,
                    statusHistory: [...(record.statusHistory || []), statusChange],
                    analysisStarted: apiStatus === 'UNDER_ANALYSIS' ? true : (record.analysisStarted || false),
                    analysisComplete: apiStatus === 'ACTION_PENDING' ? true : (record.analysisComplete || false),
                    lastStatusChangeTime: now,
                    ...actionDetails
                }
                setSelectedAlert({
                    ...selectedAlert, // Keep existing fields
                    ...updatedRecord // Override with updated data
                })
            }
        }

        if (!isSilent) {
            if (isClosedStatus(apiStatus)) {
                // Completing/closing an alert removes it from the board, so close the drawer.
                message.success(`Alert completed: ${action}`)
                setTimeout(() => {
                    if (selectedAlert && (selectedAlert.id === record.id || selectedAlert._id === record._id)) {
                        setDrawerVisible(false)
                        setDrawerExpanded(false)
                        setSelectedAlert(null)
                    }
                }, 1000)
            } else if (apiStatus === 'ACTION_TAKEN') {
                message.success(`Alert marked as Action Taken: ${action}`)
            } else {
                message.success(`${action} completed`)
            }
            // Reconcile with the server so the card lands in the correct column
            // immediately (e.g. New -> Under Analysis) without a manual refresh.
            // Preserve the user's active filters and skip the board spinner.
            loadData({ showSpinner: false, resetFilters: false })
        }
        return true
    }, [selectedAlert, loadData])

    // Assign the alert to a user (adds a new assignee record on the server).
    const handleAssign = useCallback(async (record, { user, remarks } = {}) => {
        const alertId = record && (record._id || record.id)
        if (!alertId) {
            message.error('Alert ID not found')
            return false
        }
        if (!user) {
            message.error('Please select a user to assign')
            return false
        }
        try {
            const response = await axios.post(`/alerts/${alertId}/assign`, { user, remarks })
            const updatedAlert = (response && response.data && (response.data.data || response.data)) || null
            if (updatedAlert) {
                const current = resolveAlertCurrentAssignee(updatedAlert)
                const patch = {
                    assignee: (current && current.userName) || record.assignee,
                    assigneeUserIds: current ? [current.userId] : record.assigneeUserIds,
                    assigneeRecords: normalizeAssigneeRecords(updatedAlert),
                }
                setData((prev) => prev.map((r) => (
                    (r._id === record._id || r.id === record.id) ? { ...r, ...patch } : r
                )))
                setSelectedAlert((prev) => prev ? { ...prev, ...patch } : prev)
            }
            message.success('Alert assigned successfully')
            return true
        } catch (error) {
            console.error('Error assigning alert:', error)
            message.error('Failed to assign alert')
            return false
        }
    }, [])

    const handleBulkIgnore = useCallback(() => {
        const totalFiltered = filteredData.length
        if (totalFiltered === 0) {
            message.info('No filtered alerts available')
            return
        }

        // Same authority as "Complete Action": only alerts the current user may
        // complete/ignore (original assignee, or supervisor-with-permission — the
        // server's `canComplete`). NEW-but-unauthorized alerts are skipped.
        const eligibleNewAlerts = filteredData.filter(isAlertEligibleForIgnore)
        const alertsToIgnore = eligibleNewAlerts.filter((alert) => alert.canComplete === true)
        const unauthorizedCount = eligibleNewAlerts.length - alertsToIgnore.length
        const skippedAlerts = filteredData.filter((alert) => !isAlertEligibleForIgnore(alert))
        const eligibleCount = alertsToIgnore.length
        const skippedCount = skippedAlerts.length + unauthorizedCount

        // Captured by the textarea below; read in onOk when the user confirms.
        let ignoreComment = ''

        Modal.confirm({
            title: 'Bulk Ignore Alerts',
            content: (
                <div>
                    <div>Total filtered alerts: <strong>{totalFiltered}</strong></div>
                    <div>Accepted for ignore (NEW &amp; authorized): <strong>{eligibleCount}</strong></div>
                    <div>Skipped (not NEW): <strong>{skippedAlerts.length}</strong></div>
                    <div>Skipped (not your alert to complete): <strong>{unauthorizedCount}</strong></div>
                    <div style={{ marginTop: 12, marginBottom: 4 }}>Comment <span style={{ color: '#f5222d' }}>*</span>:</div>
                    <TextArea
                        rows={3}
                        placeholder="Add a comment for ignoring these alerts (required)"
                        onChange={(e) => { ignoreComment = e.target.value }}
                    />
                    <div style={{ marginTop: 8 }}>Do you want to continue?</div>
                </div>
            ),
            okText: 'Ignore All',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                if (eligibleCount === 0) {
                    message.info('No eligible NEW alerts to ignore')
                    return
                }
                const comment = (ignoreComment || '').trim()
                if (!comment) {
                    message.error('Please add a comment before ignoring the alerts')
                    // Reject so the modal stays open for the user to enter a comment.
                    return Promise.reject(new Error('Comment required'))
                }
                setBulkIgnoreLoading(true)
                try {
                    // Single request for all eligible alerts instead of one per alert.
                    const alertIds = alertsToIgnore.map((alert) => alert._id || alert.id).filter(Boolean)
                    const response = await axios.put('/alerts/bulk/status', {
                        alertIds,
                        currentStatus: 'IGNORED',
                        remarks: comment,
                        append: false
                    })
                    const result = (response && response.data && (response.data.data || response.data)) || {}
                    const successCount = typeof result.success === 'number' ? result.success : alertIds.length
                    const failedCount = typeof result.failed === 'number' ? result.failed : 0

                    if (selectedAlert && alertsToIgnore.some((alert) => getAlertKey(alert) === getAlertKey(selectedAlert))) {
                        setDrawerVisible(false)
                        setDrawerExpanded(false)
                        setSelectedAlert(null)
                        setActiveDrawerAlertId(null)
                    }

                    await loadData()
                    message.success(`${successCount} alert${successCount !== 1 ? 's' : ''} ignored. Skipped: ${skippedCount}. Failed: ${failedCount}`)
                } catch (error) {
                    console.error('Error in bulk ignore:', error)
                    message.error('Failed to complete bulk ignore')
                } finally {
                    setBulkIgnoreLoading(false)
                }
            }
        })
    }, [filteredData, isAlertEligibleForIgnore, loadData, selectedAlert])

    // Bulk "Complete Action Directly" for NEW alerts. Mirrors bulk ignore but
    // records the action taken and moves each eligible alert to DIRECT_ACTION.
    const handleBulkDirectAction = useCallback(() => {
        const totalFiltered = filteredData.length
        if (totalFiltered === 0) {
            message.info('No filtered alerts available')
            return
        }

        // Direct action is a NEW-only operation, same eligibility as bulk ignore,
        // AND the same authority as "Complete Action": only alerts the current user
        // may complete (server's `canComplete`). NEW-but-unauthorized are skipped.
        const eligibleNewAlerts = filteredData.filter(isAlertEligibleForIgnore)
        const alertsToAction = eligibleNewAlerts.filter((alert) => alert.canComplete === true)
        const unauthorizedCount = eligibleNewAlerts.length - alertsToAction.length
        const skippedAlerts = filteredData.filter((alert) => !isAlertEligibleForIgnore(alert))
        const eligibleCount = alertsToAction.length
        const skippedCount = skippedAlerts.length + unauthorizedCount

        // Captured by the textarea below; read in onOk when the user confirms.
        let actionTakenComment = ''

        Modal.confirm({
            title: 'Bulk Complete Action Directly',
            content: (
                <div>
                    <div>Total filtered alerts: <strong>{totalFiltered}</strong></div>
                    <div>Accepted for direct action (NEW &amp; authorized): <strong>{eligibleCount}</strong></div>
                    <div>Skipped (not NEW): <strong>{skippedAlerts.length}</strong></div>
                    <div>Skipped (not your alert to complete): <strong>{unauthorizedCount}</strong></div>
                    <div style={{ marginTop: 12, marginBottom: 4 }}>Action taken <span style={{ color: '#f5222d' }}>*</span>:</div>
                    <TextArea
                        rows={3}
                        placeholder="Enter the action taken for these alerts (required)"
                        onChange={(e) => { actionTakenComment = e.target.value }}
                    />
                    <div style={{ marginTop: 8 }}>Do you want to continue?</div>
                </div>
            ),
            okText: 'Complete All',
            cancelText: 'Cancel',
            onOk: async () => {
                if (eligibleCount === 0) {
                    message.info('No eligible NEW alerts for direct action')
                    return
                }
                const actionTaken = (actionTakenComment || '').trim()
                if (!actionTaken) {
                    message.error('Please enter the action taken before completing')
                    // Reject so the modal stays open for the user to enter the action.
                    return Promise.reject(new Error('Action taken required'))
                }
                setBulkDirectActionLoading(true)
                try {
                    const alertIds = alertsToAction.map((alert) => alert._id || alert.id).filter(Boolean)
                    const response = await axios.put('/alerts/bulk/status', {
                        alertIds,
                        currentStatus: 'DIRECT_ACTION',
                        actionTaken,
                        remarks: 'Direct Action',
                        append: false
                    })
                    const result = (response && response.data && (response.data.data || response.data)) || {}
                    const successCount = typeof result.success === 'number' ? result.success : alertIds.length
                    const failedCount = typeof result.failed === 'number' ? result.failed : 0

                    if (selectedAlert && alertsToAction.some((alert) => getAlertKey(alert) === getAlertKey(selectedAlert))) {
                        setDrawerVisible(false)
                        setDrawerExpanded(false)
                        setSelectedAlert(null)
                        setActiveDrawerAlertId(null)
                    }

                    await loadData()
                    message.success(`${successCount} alert${successCount !== 1 ? 's' : ''} completed. Skipped: ${skippedCount}. Failed: ${failedCount}`)
                } catch (error) {
                    console.error('Error in bulk direct action:', error)
                    message.error('Failed to complete bulk direct action')
                } finally {
                    setBulkDirectActionLoading(false)
                }
            }
        })
    }, [filteredData, isAlertEligibleForIgnore, loadData, selectedAlert])

    // Open the bulk-action modal for a section (called from the column header)
    const handleBulkAction = useCallback((columnId, cards) => {
        if (!cards || cards.length === 0) {
            message.info('No alerts available in this section')
            return
        }
        setBulkColumnId(columnId)
        setBulkAlerts(cards)
        setBulkAnalysisText('')
        setBulkActionSuggestedText('')
        setBulkActionTakenText('')
        setBulkModalVisible(true)
    }, [])

    const handleBulkActionCancel = useCallback(() => {
        if (bulkActionLoading) return
        setBulkModalVisible(false)
        setBulkColumnId(null)
        setBulkAlerts([])
        setBulkAnalysisText('')
        setBulkActionSuggestedText('')
        setBulkActionTakenText('')
    }, [bulkActionLoading])

    // Apply the entered analysis / action to every alert in the section
    const handleBulkActionConfirm = useCallback(async () => {
        const isUnderAnalysis = bulkColumnId === 'UNDER_ANALYSIS'
        const analysis = bulkAnalysisText.trim()
        const actionSuggested = bulkActionSuggestedText.trim()
        const actionTaken = bulkActionTakenText.trim()

        if (isUnderAnalysis) {
            if (!analysis && !actionSuggested) {
                message.warning('Enter Analysis or Action Suggested')
                return
            }
        } else if (!actionTaken) {
            message.warning('Enter Action Taken')
            return
        }

        // Under Analysis -> complete analysis and move alerts to Action Pending.
        // Action Pending -> record action taken, moving them to the Action Taken
        // column (still open; they are closed later via "Complete Action").
        // The backend appends the new text to each alert's existing content.
        const alertIds = bulkAlerts.map((alert) => alert._id || alert.id).filter(Boolean)
        if (!alertIds.length) {
            message.info('No alerts available in this section')
            return
        }

        const payload = isUnderAnalysis
            ? {
                alertIds,
                currentStatus: 'ACTION_PENDING',
                remarks: 'Complete Analysis',
                analysis,
                actionSuggested,
                append: true
            }
            : {
                alertIds,
                currentStatus: 'ACTION_TAKEN',
                remarks: 'Update action taken',
                actionTaken,
                append: true
            }

        setBulkActionLoading(true)
        try {
            const response = await axios.put('/alerts/bulk/status', payload)
            const result = (response && response.data && (response.data.data || response.data)) || {}
            const successCount = typeof result.success === 'number' ? result.success : alertIds.length
            const failedCount = typeof result.failed === 'number' ? result.failed : 0

            await loadData()
            message.success(`${successCount} alert${successCount !== 1 ? 's' : ''} updated${failedCount ? `. Failed: ${failedCount}` : ''}`)
            setBulkModalVisible(false)
            setBulkColumnId(null)
            setBulkAlerts([])
            setBulkAnalysisText('')
            setBulkActionSuggestedText('')
            setBulkActionTakenText('')
        } catch (error) {
            console.error('Error in bulk action:', error)
            message.error('Failed to complete bulk action')
        } finally {
            setBulkActionLoading(false)
        }
    }, [bulkColumnId, bulkAlerts, bulkAnalysisText, bulkActionSuggestedText, bulkActionTakenText, loadData])

    // Update analysis fields
    const handleAnalysisUpdate = useCallback((alertId, updates) => {
        setData((prevData) => {
            return prevData.map((r) => {
                if (r.id !== alertId && r._id !== alertId) return r
                return { ...r, ...updates }
            })
        })

        if (selectedAlert && (selectedAlert.id === alertId || selectedAlert._id === alertId)) {
            setSelectedAlert({ ...selectedAlert, ...updates })
        }
    }, [selectedAlert])

    // Handle drawer open
    const handleOpenDrawer = useCallback((alert) => {
        setSelectedAlert(alert)
        const key = getAlertKey(alert)
        setActiveDrawerAlertId(key || null)
        setDrawerVisible(true)
    }, [])

    const handleDrawerTabChange = useCallback((alertId) => {
        const nextKey = String(alertId || '')
        const nextAlert = drawerAlerts.find((item) => getAlertKey(item) === nextKey)
        setActiveDrawerAlertId(nextKey || null)
        if (nextAlert) {
            setSelectedAlert(nextAlert)
        }
    }, [drawerAlerts])

    // Handle drawer close
    const handleCloseDrawer = useCallback(() => {
        setDrawerVisible(false)
        setDrawerExpanded(false)
        setSelectedAlert(null)
        setActiveDrawerAlertId(null)
    }, [])

    // Handle clear filters
    const handleClearFilters = useCallback(() => {
        setSearchTerm('')
        setMyAlertsOnly(false)
        setAlertDateRange([null, null])
        setEnabledFilterValues({})
        initializeDefaultFilters(data)
    }, [data, initializeDefaultFilters])

    // Toggle column expand
    const handleToggleColumnExpand = useCallback((columnId) => {
        setExpandedColumn((prev) => prev === columnId ? null : columnId)
    }, [])

    const totalAlerts = useMemo(() => {
        return data.filter(r => !isClosedStatus(r.status)).length
    }, [data])

    useEffect(() => {
        if (!drawerVisible || !drawerAlerts.length) return
        const hasActive = drawerAlerts.some(
            (item) => getAlertKey(item) === String(activeDrawerAlertId || '')
        )
        if (!hasActive) {
            const firstAlert = drawerAlerts[0]
            const firstAlertId = getAlertKey(firstAlert)
            setActiveDrawerAlertId(firstAlertId || null)
            if (firstAlert) {
                setSelectedAlert(firstAlert)
            }
        }
    }, [drawerVisible, drawerAlerts, activeDrawerAlertId])

    return (
        <div style={{ background: pageBackground, minHeight: '100vh', height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Content style={{ padding: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Title Section */}
                <div
                    style={{
                        padding: '16px 20px',
                        borderBottom: `1px solid ${border}`,
                        background: surface,
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backdropFilter: 'blur(8px)'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Icon type="inbox" style={{ fontSize: 20, color: brand }} />
                        <span style={{ fontSize: 20, fontWeight: 600, color: strong }}>
                            Alert Inbox
                        </span>
                        <Badge
                            count={totalAlerts}
                            style={{
                                backgroundColor: brand,
                                fontSize: 11,
                                fontWeight: 600
                            }}
                        />
                    </div>
                </div>

                {/* Filter Bar */}
                <FilterBar
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    activeFilterTypes={activeFilterTypes}
                    onFilterTypeToggle={handleFilterTypeChange}
                    onReset={handleClearFilters}
                    onRefresh={handleRefresh}
                    loading={loading}
                    myAlertsOnly={myAlertsOnly}
                    onToggleMyAlerts={setMyAlertsOnly}
                    isDark={isDark}
                />

                {/* Filter Chips */}
                <FilterChips
                    data={data}
                    activeFilterTypes={activeFilterTypes}
                    enabledFilterValues={enabledFilterValues}
                    searchTerm={searchTerm}
                    onToggleFilterValue={toggleFilterValue}
                    onSelectAll={selectAllFilterValues}
                    onSelectNone={selectNoneFilterValues}
                    getDynamicCount={getDynamicCount}
                    alertDateRange={alertDateRange}
                    onAlertDateRangeChange={setAlertDateRange}
                    isDark={isDark}
                />

                {/* Alert Board */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Icon type="loading" style={{ fontSize: 32 }} spin />
                    </div>
                ) : filteredData.length > 0 ? (
                    <AlertBoard
                        alerts={filteredData}
                        relatedCountsByAlertId={relatedCountsByAlertId}
                        expandedColumn={expandedColumn}
                        onToggleColumnExpand={handleToggleColumnExpand}
                        onCardClick={handleOpenDrawer}
                        onBulkAction={handleBulkAction}
                        onBulkIgnore={handleBulkIgnore}
                        bulkIgnoreLoading={bulkIgnoreLoading}
                        onBulkDirectAction={handleBulkDirectAction}
                        bulkDirectActionLoading={bulkDirectActionLoading}
                        isDark={isDark}
                    />
                ) : (
                    <Card style={{ margin: '20px', background: isDark ? '#111827' : '#ffffff', borderColor: border }}>
                        <Empty
                            image={<Icon type="inbox" style={{ fontSize: 48, color: muted }} />}
                            description={
                                <div>
                                    <div style={{ fontWeight: 600, marginBottom: 4, color: strong }}>
                                        No alerts found
                                    </div>
                                    <div style={{ color: muted }}>Try clearing filters or adjusting your search.</div>
                                </div>
                            }
                        />
                    </Card>
                )}

                {/* Alert Drawer */}
                <AlertDrawer
                    alert={activeDrawerAlert}
                    relatedAlerts={drawerAlerts}
                    activeAlertId={activeDrawerAlertId}
                    visible={drawerVisible}
                    expanded={drawerExpanded}
                    onClose={handleCloseDrawer}
                    onTabChange={handleDrawerTabChange}
                    onToggleExpand={() => setDrawerExpanded(prev => !prev)}
                    onStatusUpdate={updateAlertStatus}
                    onAnalysisUpdate={handleAnalysisUpdate}
                    onAssign={handleAssign}
                    isDark={isDark}
                />

                {/* Bulk Action Modal (per section) */}
                <Modal
                    title={bulkColumnId === 'UNDER_ANALYSIS' ? 'Bulk Complete Analysis' : 'Bulk Complete Action'}
                    visible={bulkModalVisible}
                    onOk={handleBulkActionConfirm}
                    onCancel={handleBulkActionCancel}
                    okText={`Apply to ${bulkAlerts.length} alert${bulkAlerts.length !== 1 ? 's' : ''}`}
                    confirmLoading={bulkActionLoading}
                    cancelButtonProps={{ disabled: bulkActionLoading }}
                    maskClosable={!bulkActionLoading}
                    destroyOnClose
                >
                    <div style={{ marginBottom: 12, color: muted }}>
                        The values below will be applied to all <strong>{bulkAlerts.length}</strong> filtered alert{bulkAlerts.length !== 1 ? 's' : ''} in the
                        {' '}<strong>{bulkColumnId === 'UNDER_ANALYSIS' ? 'Under Analysis' : 'Action Pending'}</strong> section.
                        {bulkColumnId === 'UNDER_ANALYSIS'
                            ? ' Completing the analysis will move them to Action Pending.'
                            : ' Completing the action will move them to Action Taken.'}
                        {' '}The text is appended to any existing content on each alert.
                    </div>
                    {bulkColumnId === 'UNDER_ANALYSIS' ? (
                        <>
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ marginBottom: 8, fontWeight: 600 }}>Analysis</div>
                                <TextArea
                                    rows={4}
                                    value={bulkAnalysisText}
                                    onChange={(e) => setBulkAnalysisText(e.target.value)}
                                    placeholder="Enter your analysis here..."
                                />
                            </div>
                            <div>
                                <div style={{ marginBottom: 8, fontWeight: 600 }}>Action Suggested</div>
                                <TextArea
                                    rows={4}
                                    value={bulkActionSuggestedText}
                                    onChange={(e) => setBulkActionSuggestedText(e.target.value)}
                                    placeholder="Enter suggested action here..."
                                />
                            </div>
                        </>
                    ) : (
                        <div>
                            <div style={{ marginBottom: 8, fontWeight: 600 }}>Action Taken</div>
                            <TextArea
                                rows={4}
                                value={bulkActionTakenText}
                                onChange={(e) => setBulkActionTakenText(e.target.value)}
                                placeholder="Enter action taken here..."
                            />
                        </div>
                    )}
                </Modal>
            </Content>
        </div>
    )
}

const mapStateToProps = ({ settings }) => {
    const { themeType } = settings
    return { themeType }
}

export default connect(mapStateToProps)(AlertInbox)
